<!-- eslint-disable max-len -->
<template>
    <div class="sailings-table__container">
        <div class="sailings-table" v-for="(departureGroup, tableIndex) in departureGroups" :key="tableIndex">
            <ul class="sailings-table__header">
                <li class="port">{{ $t("sailingsTable.headers.from") }} {{ departureGroup.port.replace(/ .*/,'') }}</li>
                <li class="time">{{ $t("sailingsTable.headers.departure") }}</li>
                <li class="time">{{ $t("sailingsTable.headers.arrival") }}</li>
                <li class="ship">{{ $t("sailingsTable.headers.ship") }}</li>
                <li class="status">{{ $t("sailingsTable.headers.status") }}</li>
                <li class="info"></li>
            </ul>
            <div v-for="(row, index) in departureGroup.rows" :key="index" class="sailings-table__row" :class="isDisabled(row.status)">
                <ul>
                    <li class="port">{{ row.port }}</li>
                    <li class="time">{{ row.destinationTime }}</li>
                    <li class="time">{{ row.displayTime }}</li>
                    <li class="ship">{{ row.ship }}</li>
                    <li class="status">{{ row.status }}</li>
                    <li class="info">
                        <button v-if="row.more" @click="toggleInfo((tableIndex + 1) * index)">
                            <SvgIcon :icon="'carbon/info-filled'" width="1.25rem" height="1.25rem" />
                            <span>{{ $t("sailingsTable.info.buttonText") }}</span>
                        </button>
                    </li>
                </ul>
                <div v-if="row.more" class="sailings-table__info" :class="{ 'active': !!activeMap[(tableIndex + 1) * index] }">
                    <div class="info-container">
                        <div class="info-container__header">
                            <SvgIcon :icon="'carbon/info-filled'" width="1.25rem" height="1.25rem" />
                            <span>{{ $t("sailingsTable.info.headerText") }}</span>
                        </div>
                        <div id="" class="info-container__content">
                            <p v-html="row.more"></p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { SailingsUpdateDepartureResponse } from "@/models/api/SailingsUpdateDepartureResponse";
import { computed, reactive } from "vue";
import _ from "lodash";
import SvgIcon from "@/components/icons/SvgIcon.vue";

interface Props {
    departures?: SailingsUpdateDepartureResponse[];
    isLoading?: boolean;
}

const props = defineProps<Props>();

const departureGroups = computed(() => {
    if(!props.departures) return undefined;

    return _.chain(props.departures)
        .groupBy("port")
        .map((rows: [], port: string) => ({ port, rows }))
        .value();
});

const isDisabled = (status: string) => {
    if (!status) return "";

    switch (status.toLowerCase()) {
        case "arrived":
        case "cancelled":
        case "no sailing":
            return "disabled";
        default:
            return "";
    }
};

const activeMap = reactive<boolean[]>([]);

const toggleInfo = (index: number) => {
    activeMap[index] = !activeMap[index]
}
</script>

<style lang="scss" scoped>
.sailings-table {
    --sailings-table__columns: 6;

    display: flex;
    flex-direction: column;

    &__container {
        display: flex;
        flex-direction: column;
        row-gap: 20px;
    }

    &__header {
        display: flex;
        flex-direction: row;
        margin: 0;
        padding: 10px 0;
        border-top: 1px solid var(--c-text);
        border-bottom: 1px solid var(--c-text);

        li {
            padding: 0 15px;
            width: 100%;
            max-width: calc(100% / var(--sailings-table__columns));
            font-weight: bold;

            &.port {
                max-width: 240px;
            }

            &.time,
            &.ship,
            &.status {
                max-width: 120px;
            }

            &.info {
                display: flex;
                justify-content: flex-end;
                max-width: unset;
            }
        }
    }

    &__row {
        display: flex;
        flex-direction: column;

        &:nth-child(2n) {
            background-color: var(--c-background);
        }
        
        ul {
            display: flex;
            flex-direction: row;
            margin: 0;
            padding: 10px 0;

            li {
                padding: 0 15px;
                width: 100%;
                max-width: calc(100% / var(--sailings-table__columns));

                &.port {
                    max-width: 240px;
                }

                &.time,
                &.ship,
                &.status {
                    max-width: 120px;
                }

                &.info {
                    display: flex;
                    justify-content: flex-end;
                    max-width: unset;
                }
    
                button {
                    display: flex;
                    align-items: center;
                    column-gap: 5px;
                    background-color: transparent;
                    color: var(--c-danger);
                    border: none;
                    text-decoration: underline;
                    cursor: pointer;
                }
            }
        }

        &.disabled {
            li:not(.info) {
                color: var(--c-text);
                opacity: 0.5;
            }
        }
    }
    
    &__info {
        display: none;
        background-color: var(--c-base);
        padding: 10px 15px;

        &.active {
            display: block;
        }
    }

    .info-container {
        border-radius: var(--border-radius);

        &__header {
            display: flex;
            align-items: center;
            column-gap: 5px;
            padding: 10px 15px;
            background-color: var(--c-danger);
            color: var(--c-base);
            border-top-left-radius: inherit;
            border-top-right-radius: inherit;
            font-weight: bold;

            :deep(.icon) {
                fill: var(--c-base);
            }
        }

        &__content {
            padding: 10px 15px;
            border: 1px solid var(--c-danger);
            border-top: none;
            border-bottom-left-radius: inherit;
            border-bottom-right-radius: inherit;

            p {
                margin: 0;
                color: var(--c-text);

                + p {
                    margin-top: 15px;
                }
            }
        }
    }
}
</style>
